import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { RefreshIconButton } from '../components/RefreshIconButton';
import { scheduleDeferredWork } from '../utils/deferredWork';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Videocam as CameraIcon,
  Delete as DeleteIcon,
  CheckCircle as RunningIcon,
  HourglassEmpty as DeployingIcon,
  Error as FailedIcon,
  CloudOff as OfflineIcon,
  HelpOutline as UnknownIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Policy as PolicyIcon,
  History as AuditIcon,
  Close as CloseIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import {
  camerasApi,
  Camera,
  CameraStatus,
  CameraTestResult,
  CameraCreateData,
  Epic46CameraProfile,
  formatRegistryCameraDeleteError,
} from '../services/camerasApi';
import { devicesApi } from '../services/devicesApi';
import { api, ensureUiScope } from '../services/api';
import * as mjpegApi from '../services/mjpegApi';
import { zonesApi } from '../services/zonesApi';
import { linesApi } from '../services/linesApi';
import { policiesApi } from '../services/policiesApi';
import { ZoneSnapshotEditorDialog } from '../components/ZoneSnapshotEditorDialog';
import { getCameraHealthMetadata, CameraHealthState } from '../utils/statusMetadata';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';
import { canAccessWarehouseSurface, getCapabilitiesFromUser } from '../auth/access';
import { preferredMjpegModeForCamera } from '../services/mjpegMode';
import { useSiteLocationLookups } from '../hooks/useSiteLocationLookups';
import { sitesApi, type SiteArea } from '../services/sitesApi';
import { hasCapability } from '../auth/access';
import { CameraLocationDialog } from '../components/location/CameraLocationDialog';
import { CameraLocationPath } from '../components/location/CameraLocationPath';

// F-08: Snapshot staleness threshold (keep consistent with Snapshot Editor UX)
const SNAPSHOT_STALE_MS = 60_000;

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'running':
      return <RunningIcon color="success" />;
    case 'deploying':
      return <DeployingIcon color="warning" />;
    case 'failed':
      return <FailedIcon color="error" />;
    case 'offline':
      return <OfflineIcon color="disabled" />;
    case 'disabled':
      return <OfflineIcon color="disabled" />;
    default:
      return <UnknownIcon color="disabled" />;
  }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status) {
    case 'running':
      return 'success';
    case 'deploying':
      return 'warning';
    case 'failed':
      return 'error';
    case 'offline':
      return 'default';
    case 'disabled':
      return 'default';
    default:
      return 'info';
  }
};

const getHealthStateColor = (
  state: string
): 'success' | 'warning' | 'error' | 'default' => {
  switch (String(state || '').toUpperCase()) {
    case 'READY':
      return 'success';
    case 'DEGRADED':
      return 'warning';
    case 'AUTH_FAILED':
    case 'STREAM_ERROR':
      return 'error';
    case 'OFFLINE':
    default:
      return 'default';
  }
};

const formatTimestamp = (ts?: string): string => {
  if (!ts) return 'Never';
  try {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    return date.toLocaleDateString();
  } catch {
    return ts;
  }
};

const Cameras: React.FC = () => {
  const { effectiveUser: user } = useAuth();
  const capabilities = getCapabilitiesFromUser(user);
  const canReadCameras = canAccessWarehouseSurface(capabilities, 'cameras');
  const canWriteCameras =
    capabilities.includes('cameras:write') || capabilities.includes('camera:manage');
  const canWriteSites = hasCapability(capabilities, 'sites:write');
  const uiScope = ensureUiScope();
  const siteId = String(uiScope.siteId || process.env.REACT_APP_SITE_ID || '').trim();
  const { refresh: refreshLocationLookups, ...locationLookups } = useSiteLocationLookups(
    siteId,
    Boolean(siteId),
  );
  const navigate = useNavigate();
  const { mode: appMode } = useMode();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [devices, setDevices] = useState<Array<{ device_id: string; name: string }>>([]);
  const [profileOptions, setProfileOptions] = useState<Epic46CameraProfile[]>([]);
  const [verifySubmitting, setVerifySubmitting] = useState<Record<string, boolean>>({});
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<Camera | null>(null);
  const [verifyResult, setVerifyResult] = useState<any | null>(null);
  const grafanaBase = (process.env.REACT_APP_GRAFANA_URL || '').trim().replace(/\/+$/, '');

  // Add camera form state
  const [formData, setFormData] = useState<CameraCreateData>({
    name: '',
    rtsp_url: '',
    edge_device_id: '',
    profile_id: 'A',
    rtsp_username: '',
    rtsp_password: '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<CameraTestResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Camera status + thumbnails
  const [statusMap, setStatusMap] = useState<Record<string, CameraStatus>>({});
  const [snapshotMap, setSnapshotMap] = useState<Record<string, string>>({});
  const [snapshotAgeMap, setSnapshotAgeMap] = useState<Record<string, number>>({});
  const [clockStatusByDevice, setClockStatusByDevice] = useState<Record<string, boolean | null>>({});

  // Enable/disable dialog
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<Camera | null>(null);
  const [toggleEnabledTarget, setToggleEnabledTarget] = useState<boolean>(true);
  const [toggleReason, setToggleReason] = useState<string>('');
  const [toggleSubmitting, setToggleSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Camera | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState<string | null>(null);
  const [pipelineSubmitting, setPipelineSubmitting] = useState<Record<string, boolean>>({});

  // Polling/backoff UI state (optional display)
  const [, setIsPolling] = useState(false);
  const [pollBackoffUntil, setPollBackoffUntil] = useState<number | null>(null);

  // Camera viewer dialog (MUST be declared before refs that read it)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingCamera, setViewingCamera] = useState<Camera | null>(null);
  const [mjpegUrl, setMjpegUrl] = useState<string | null>(null);
  const [mjpegLoading, setMjpegLoading] = useState(false);
  const [mjpegError, setMjpegError] = useState<string | null>(null);
  const [isLiveStream, setIsLiveStream] = useState(false);

  // Snapshot Editor (camera-first, full-screen modal)
  const [snapshotEditorOpen, setSnapshotEditorOpen] = useState(false);
  const [snapshotEditorCamera, setSnapshotEditorCamera] = useState<Camera | null>(null);

  // Camera placement (Epic 281-B)
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationTarget, setLocationTarget] = useState<Camera | null>(null);
  const [locationAreas, setLocationAreas] = useState<SiteArea[]>([]);
  const [locationAreaId, setLocationAreaId] = useState('');
  const [locationMount, setLocationMount] = useState('');
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // F-02: Track which cameras have geometry or policies (for state-based CTAs)
  const [cameraSetupStatus, setCameraSetupStatus] = useState<Record<string, { hasGeometry: boolean; hasPolicies: boolean }>>({});

  // Refs to avoid recreating intervals when state changes
  const camerasRef = useRef<Camera[]>([]);
  useEffect(() => {
    camerasRef.current = cameras;
  }, [cameras]);

  const viewerOpenRef = useRef(false);
  useEffect(() => {
    viewerOpenRef.current = viewerOpen;
  }, [viewerOpen]);

  const statusMapRef = useRef<Record<string, CameraStatus>>({});
  useEffect(() => {
    statusMapRef.current = statusMap;
  }, [statusMap]);

  const displayedCameras = React.useMemo(() => {
    const now = Date.now();
    return cameras.filter((camera) => {
      const status = statusMap[camera.camera_id] || { status: camera.status || 'unknown' };
      const displayStatus = String(status.status || camera.status || 'unknown').toLowerCase();
      const enabled = camera.enabled !== false && displayStatus !== 'disabled';
      if (!enabled) return false;

      const snapTs = snapshotAgeMap[camera.camera_id];
      const hasFreshSnapshot = Boolean(snapTs) && (now - Number(snapTs)) <= SNAPSHOT_STALE_MS;
      const runtimeStatus = String((camera.health as any)?.status || '').toLowerCase();
      const healthState = String(
        (status as any)?.health_state?.state || (camera as any)?.health_state?.state || ''
      ).toUpperCase();

      return (
        displayStatus === 'running'
        || displayStatus === 'ready'
        || runtimeStatus === 'running'
        || runtimeStatus === 'ready'
        || healthState === 'READY'
        || hasFreshSnapshot
      );
    });
  }, [cameras, snapshotAgeMap, statusMap]);

  const hiddenCameraCount = Math.max(0, cameras.length - displayedCameras.length);

  /**
   * Fetch cameras + a small number of statuses to avoid rate limiting.
   * IMPORTANT: re-throws 429 so poll loop can backoff.
   */
  const fetchCameras = useCallback(async (opts?: { initial?: boolean }) => {
    const initial = opts?.initial ?? false;

    try {
      if (initial) setLoading(true);
      else setRefreshing(true);

      setError(null);

      if (!siteId) {
        setCameras([]);
        setStatusMap({});
        setError('Select a valid org/site scope before loading cameras.');
        return [] as Camera[];
      }

      const response = await camerasApi.listCameraBootstrap(siteId);

      if (!response?.ok) {
        setError('Failed to fetch cameras');
        return [] as Camera[];
      }

      let items = Array.isArray(response.items) ? (response.items as Camera[]) : [];
      // Warehouse mode should not show OCR cameras at all.
      if (appMode === 'warehouse') {
        items = items.filter((c) => {
          const po = String((c as any)?.pipeline_override || '').toLowerCase();
          const p = String((c as any)?.pipeline || '').toLowerCase();
          return !(po.includes('ocr') || p.includes('ocr'));
        });
      }
      setCameras(items);

      // F-02: geometry/policy hints — defer so list + status checks stay on the critical path.
      scheduleDeferredWork(() => {
        void checkCameraSetupStatus(items);
      });

      // Fetch status only for a few cameras that might be active.
      // Prefer already-running cameras so offline/unknown devices do not crowd out live tiles.
      const camerasNeedingStatus = items.filter(
        (cam) => cam.status === 'running' || cam.status === 'deploying' || cam.status === 'unknown'
      );
      const statusFetchRank = (cam: Camera) => {
        const s = String(cam.status || '').toLowerCase();
        if (s === 'running' || s === 'ready') return 0;
        if (s === 'deploying') return 1;
        return 2;
      };

      const MAX_STATUS_FETCHES = 5;
      const camerasToCheck = [...camerasNeedingStatus]
        .sort((a, b) => statusFetchRank(a) - statusFetchRank(b))
        .slice(0, MAX_STATUS_FETCHES);

      const statusResults: Array<{ camera_id: string; status: CameraStatus | null }> = [];

      for (let i = 0; i < camerasToCheck.length; i++) {
        const cam = camerasToCheck[i];

        if (i > 0) await new Promise((resolve) => setTimeout(resolve, 200));

        try {
          const status = await camerasApi.getCameraStatus(cam.camera_id, cam.edge_device_id);
          statusResults.push({ camera_id: cam.camera_id, status });
        } catch (err: any) {
          // Propagate rate-limit so polling can backoff.
          if (err?.status === 429 || err?.response?.status === 429) {
            throw err;
          }
          statusResults.push({ camera_id: cam.camera_id, status: null });
        }
      }

      const newStatusMap: Record<string, CameraStatus> = {};
      for (const { camera_id, status } of statusResults) {
        if (status) newStatusMap[camera_id] = status;
      }
      setStatusMap(newStatusMap);

      return items;
    } catch (err: any) {
      // IMPORTANT: allow 429 to propagate to poll loop
      if (err?.status === 429 || err?.response?.status === 429) {
        throw err;
      }
      const detail = String(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to fetch cameras'
      ).trim();
      const isCapabilityError =
        err?.response?.status === 403 || detail.toLowerCase().includes('insufficient_capability');
      setError(
        isCapabilityError
          ? `You do not have camera read access for site "${siteId || 'unknown'}". Ask for the \`cameras:read\` capability or switch to the correct org/site scope.`
          : detail
      );
      return [] as Camera[];
    } finally {
      if (initial) setLoading(false);
      else setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkCameraSetupStatus intentionally excluded to avoid refetch loops
  }, [appMode, siteId]);

  const setCameraMode = useCallback(
    async (cam: Camera, mode: 'warehouse' | 'ocr') => {
      if (!cam?.camera_id || !cam?.edge_device_id) return;
      const key = `${cam.edge_device_id}:${cam.camera_id}`;
      try {
        setPipelineSubmitting((m) => ({ ...m, [key]: true }));
        // warehouse = clear override (backend will derive default)
        // ocr = set override to ocr_stream
        const pipeline = mode === 'ocr' ? 'ocr_stream' : null;
        await camerasApi.setCameraPipeline(siteId, cam.camera_id, cam.edge_device_id, pipeline);
        setSnackbar({ open: true, message: `Camera mode set to ${mode.toUpperCase()}. Deploying...`, severity: 'success' });
        await fetchCameras();
      } catch (e: any) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Failed to set camera mode';
        setSnackbar({ open: true, message: String(msg), severity: 'error' });
      } finally {
        setPipelineSubmitting((m) => ({ ...m, [key]: false }));
      }
    },
    [fetchCameras, siteId]
  );

  const setCameraProfile = useCallback(
    async (cam: Camera, profileId: 'A' | 'B' | 'C' | 'D') => {
      if (!cam?.camera_id || !cam?.edge_device_id) return;
      const key = `${cam.edge_device_id}:${cam.camera_id}`;
      try {
        setPipelineSubmitting((m) => ({ ...m, [key]: true }));
        await camerasApi.setCameraProfile(siteId, cam.camera_id, cam.edge_device_id, profileId);
        setSnackbar({
          open: true,
          message: `Camera profile set to ${profileId}. Deploying...`,
          severity: 'success',
        });
        await fetchCameras();
      } catch (e: any) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Failed to set camera profile';
        setSnackbar({ open: true, message: String(msg), severity: 'error' });
      } finally {
        setPipelineSubmitting((m) => ({ ...m, [key]: false }));
      }
    },
    [fetchCameras, siteId]
  );

  const fetchClockStatus = useCallback(async (deviceIds: string[]) => {
    const unique = Array.from(new Set(deviceIds.filter(Boolean)));
    if (unique.length === 0) return;
    const updates: Record<string, boolean | null> = {};
    await Promise.all(
      unique.map(async (deviceId) => {
        try {
          const resp = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/health`);
          const unsynced = resp?.data?.clock?.clock_unsynced;
          updates[deviceId] = typeof unsynced === 'boolean' ? unsynced : null;
        } catch {
          updates[deviceId] = null;
        }
      }),
    );
    setClockStatusByDevice((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const deviceList = await devicesApi.listDevices();
      // Handle both array and object responses
      const list = Array.isArray(deviceList) 
        ? deviceList 
        : (deviceList && typeof deviceList === 'object' && 'devices' in deviceList 
          ? (deviceList as any).devices || [] 
          : []);
      const mapped = Array.isArray(list) ? list.map((d: any) => ({
        device_id: d.device_id,
        name: d.name || d.device_id,
      })) : [];
      setDevices(mapped);

      if (mapped.length > 0 && !formData.edge_device_id) {
        setFormData((prev: CameraCreateData) => ({ ...prev, edge_device_id: mapped[0].device_id }));
      }
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setDevices([]);
    }
  }, [formData.edge_device_id]);

  const fetchProfileOptions = useCallback(async () => {
    try {
      const rows = await camerasApi.getEpic46CameraProfiles();
      if (rows.length > 0) {
        setProfileOptions(rows);
        return;
      }
    } catch {
      // fall back to static list below
    }
    setProfileOptions([
      { profile_id: 'A', profile_name: 'Core Safety Camera', role: 'default_safety' },
      { profile_id: 'B', profile_name: 'Worker Protection Camera', role: 'worker_protection' },
      { profile_id: 'C', profile_name: 'Forklift ROI Safety Camera', role: 'forklift_safety' },
      { profile_id: 'D', profile_name: 'Premium / Advanced Camera', role: 'advanced' },
    ]);
  }, []);

  // F-02: Check which cameras have geometry (zones/lines) or policies
  const checkCameraSetupStatus = useCallback(async (cameraList: Camera[]) => {
    try {
      const cameraIds = cameraList.map((c) => c.camera_id);
      if (cameraIds.length === 0) return;

      // Fetch zones, lines, and policies in parallel
      const [zonesRes, linesRes, policiesRes] = await Promise.all([
        zonesApi.listZones(undefined, undefined, undefined, undefined, true).catch(() => ({ zones: [] })),
        linesApi.listLines(undefined, undefined, true).catch(() => ({ lines: [] })),
        policiesApi.listPolicies(siteId, true).catch(() => ({ policies: [] })),
      ]);

      const zones = zonesRes.zones || [];
      const lines = linesRes.lines || [];
      const policies = policiesRes.policies || [];

      // Build a map of camera_id -> { hasGeometry, hasPolicies }
      const status: Record<string, { hasGeometry: boolean; hasPolicies: boolean }> = {};
      
      cameraIds.forEach((cameraId) => {
        const hasZones = zones.some((z) => z.camera_id === cameraId && z.enabled !== false);
        const hasLines = lines.some((l) => l.camera_id === cameraId && l.enabled !== false);
        const hasPolicies = policies.some((p) => {
          // Check if policy references this camera (via zones or direct camera_id in scope)
          const scopeCameraMatch = p.scope?.camera_ids?.includes(cameraId);
          const scopeZoneMatch =
            Boolean(p.scope?.zone_ids) &&
            zones.some((z) => z.camera_id === cameraId && p.scope?.zone_ids?.includes(z.zone_id));

          // line_crossing uses config.line_id (or config.line.line_id) in this codebase.
          const policyLineId =
            (p as any)?.config?.line_id ||
            (p as any)?.config?.line?.line_id ||
            null;
          const lineMatch =
            typeof policyLineId === 'string' &&
            lines.some((l) => l.camera_id === cameraId && l.line_id === policyLineId);

          return Boolean(scopeCameraMatch || scopeZoneMatch || lineMatch);
        });
        
        status[cameraId] = {
          hasGeometry: hasZones || hasLines,
          hasPolicies: hasPolicies,
        };
      });

      setCameraSetupStatus(status);
    } catch (err) {
      console.error('Failed to check camera setup status:', err);
      // Don't fail the whole page if this check fails
    }
  }, [siteId]);

  /**
   * Legacy/fallback snapshots for thumbnails only (slow cadence, hard cap).
   * Keep if you want tiles to show something before a live MJPEG view.
   */
  const fetchSnapshots = useCallback(async (items: Camera[]) => {
    try {
      const MAX_SNAPSHOTS = 12;
      // Prefer likely-live cameras first, but still fetch for unknown/disabled so tiles populate.
      const sorted = [...items].sort((a, b) => {
        const sa = (statusMapRef.current[a.camera_id]?.status ?? a.status) || 'unknown';
        const sb = (statusMapRef.current[b.camera_id]?.status ?? b.status) || 'unknown';
        const rank = (s: string) => {
          if (s === 'running') return 0;
          if (s === 'deploying') return 1;
          if (s === 'unknown') return 2;
          if (s === 'disabled') return 3;
          if (s === 'offline') return 4;
          if (s === 'failed') return 5;
          return 6;
        };
        return rank(sa) - rank(sb);
      });

      const targets = sorted.slice(0, MAX_SNAPSHOTS);

      const results: Array<{ camera_id: string; snapshot: string | null }> = [];

      for (let i = 0; i < targets.length; i++) {
        const cam = targets[i];
        if (i > 0) await new Promise((r) => setTimeout(r, 250));
        try {
          const snap = await camerasApi.getCameraThumbnail(cam.camera_id, cam.edge_device_id);
          results.push({ camera_id: cam.camera_id, snapshot: snap });
        } catch {
          results.push({ camera_id: cam.camera_id, snapshot: null });
        }
      }

      setSnapshotMap((prev: Record<string, string>) => {
        const next = { ...prev };
        for (const r of results) {
          if (r.snapshot) next[r.camera_id] = r.snapshot;
        }
        return next;
      });

      setSnapshotAgeMap((prev: Record<string, number>) => {
        const next = { ...prev };
        for (const r of results) {
          if (r.snapshot) next[r.camera_id] = Date.now();
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch snapshots:', err);
    }
  }, []);

  /**
   * Polling setup:
   * - status poll every 30s with backoff on 429
   * - snapshot poll every 90s (optional)
   */
  useEffect(() => {
    let mounted = true;
    let statusIntervalId: ReturnType<typeof setInterval> | null = null;
    let snapshotIntervalId: ReturnType<typeof setInterval> | null = null;

    const backoffUntilRef = { current: null as number | null };
    const isPollingRef = { current: false };
    const lastSnapshotFetchRef = { current: 0 };

    const pollCameras = async () => {
      if (!mounted) return;
      if (isPollingRef.current) return;
      if (viewerOpenRef.current) return;
      if (document.visibilityState !== 'visible') return;
      if (backoffUntilRef.current && Date.now() < backoffUntilRef.current) return;

      isPollingRef.current = true;
      setIsPolling(true);

      try {
        await fetchCameras();
        backoffUntilRef.current = null;
        setPollBackoffUntil(null);
      } catch (err: any) {
        if (err?.status === 429 || err?.response?.status === 429) {
          const retryAfter = err.retryAfterSeconds || 60;
          backoffUntilRef.current = Date.now() + retryAfter * 1000;
          setPollBackoffUntil(backoffUntilRef.current);
          console.warn(`Rate limited. Backing off for ${retryAfter}s`);
        }
      } finally {
        if (mounted) {
          isPollingRef.current = false;
          setIsPolling(false);
        }
      }
    };

    const pollSnapshots = async () => {
      if (!mounted) return;
      if (viewerOpenRef.current) return;
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (now - lastSnapshotFetchRef.current < 90000) return;
      lastSnapshotFetchRef.current = now;

      const items = camerasRef.current;
      if (items.length) await fetchSnapshots(items);
    };

    // Initial fetch; thumbnails after idle so MJPEG snapshot fanout does not contend with list/status.
    (async () => {
      try {
        const items = await fetchCameras({ initial: true });
        if (mounted && items.length) {
          scheduleDeferredWork(() => {
            if (!mounted) return;
            void (async () => {
              await fetchSnapshots(items);
              lastSnapshotFetchRef.current = Date.now();
            })();
          });
        }
      } catch (err: any) {
        // If initial hits 429, just surface error; polling loop will handle later.
        if (!(err?.status === 429 || err?.response?.status === 429)) {
          console.error('Initial fetch failed:', err);
        }
      }
    })();

    fetchDevices();
    fetchProfileOptions();

    statusIntervalId = setInterval(pollCameras, 30000);
    snapshotIntervalId = setInterval(pollSnapshots, 90000);

    return () => {
      mounted = false;
      if (statusIntervalId) clearInterval(statusIntervalId);
      if (snapshotIntervalId) clearInterval(snapshotIntervalId);
    };
  }, [fetchCameras, fetchDevices, fetchProfileOptions, fetchSnapshots]);

  useEffect(() => {
    const deviceIds = cameras.map((c) => c.edge_device_id).filter(Boolean);
    if (deviceIds.length === 0) return;
    scheduleDeferredWork(() => {
      void fetchClockStatus(deviceIds);
    }, { timeoutMs: 1500 });
  }, [cameras, fetchClockStatus]);

  // Test camera connection
  const handleTestCamera = async () => {
    if (!formData.rtsp_url || !formData.edge_device_id) {
      setSnackbar({ open: true, message: 'Please enter RTSP URL and select edge device', severity: 'error' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      let testUrl = formData.rtsp_url;
      if (formData.rtsp_username && formData.rtsp_password) {
        try {
          const url = new URL(testUrl);
          url.username = formData.rtsp_username;
          url.password = formData.rtsp_password;
          testUrl = url.toString();
        } catch {
          const match = testUrl.match(/^(rtsp:\/\/)(.*@)?(.*)$/);
          if (match) testUrl = `rtsp://${formData.rtsp_username}:${formData.rtsp_password}@${match[3]}`;
        }
      }

      const result = await camerasApi.testCamera(siteId, testUrl, formData.edge_device_id);
      setTestResult(result);

      if (result.ok) setSnackbar({ open: true, message: 'Camera test successful!', severity: 'success' });
      else setSnackbar({ open: true, message: result.error || result.detail || 'Camera test failed', severity: 'error' });
    } catch (err: any) {
      setTestResult({ ok: false, error: 'test_failed', detail: err.message });
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || err.message || 'Camera test failed',
        severity: 'error',
      });
    } finally {
      setTesting(false);
    }
  };

  // Add camera
  const handleAddCamera = async () => {
    if (!formData.name || !formData.rtsp_url || !formData.edge_device_id) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      let rtspUrl = formData.rtsp_url;
      if (formData.rtsp_username && formData.rtsp_password) {
        try {
          const url = new URL(rtspUrl);
          url.username = formData.rtsp_username;
          url.password = formData.rtsp_password;
          rtspUrl = url.toString();
        } catch {
          const match = rtspUrl.match(/^(rtsp:\/\/)(.*@)?(.*)$/);
          if (match) rtspUrl = `rtsp://${formData.rtsp_username}:${formData.rtsp_password}@${match[3]}`;
        }
      }

      await camerasApi.addCamera(siteId, {
        name: formData.name,
        rtsp_url: rtspUrl,
        edge_device_id: formData.edge_device_id,
        pipeline: formData.pipeline,
        profile_id: formData.profile_id || 'A',
      });

      setSnackbar({ open: true, message: 'Camera added successfully! Deployment started.', severity: 'success' });
      setOpenAddDialog(false);
      setFormData({
        name: '',
        rtsp_url: '',
        edge_device_id: '',
        profile_id: 'A',
        rtsp_username: '',
        rtsp_password: '',
        pipeline: undefined,
      });
      setTestResult(null);

      setTimeout(() => {
        fetchCameras().catch(() => undefined);
      }, 1000);
    } catch (err: any) {
      const d = err?.response?.data;
      const allowed = Array.isArray(d?.allowed) ? d.allowed.join(', ') : '';
      const pipelineMsg =
        d?.error === 'pipeline_not_allowed'
          ? `Pipeline not allowed${allowed ? ` (use: ${allowed})` : ''}.`
          : '';
      const siteMsg =
        d?.error === 'device_site_mismatch'
          ? `Device belongs to site "${d.device_site_id || 'unknown'}" but UI is scoped to "${d.requested_site_id || siteId}". Switch org/site scope or pick a device in this site.`
          : '';
      setSnackbar({
        open: true,
        message:
          pipelineMsg ||
          siteMsg ||
          (typeof d?.detail === 'string' ? d.detail : '') ||
          (typeof d?.error === 'string' ? d.error : '') ||
          err.message ||
          'Failed to add camera',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormData({
      name: '',
      rtsp_url: '',
      edge_device_id: '',
      profile_id: 'A',
      rtsp_username: '',
      rtsp_password: '',
      pipeline: undefined,
    });
    setTestResult(null);
  };

  const handleRequestDeleteCamera = (camera: Camera) => {
    setDeleteDialogError(null);
    setDeleteTarget(camera);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteCamera = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    setDeleteDialogError(null);
    try {
      const res = await camerasApi.deleteCamera(deleteTarget.edge_device_id, deleteTarget.camera_id);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setDeleteDialogError(null);
      const zonesDisabled = Number(res?.cleanup?.zones_disabled || 0);
      const policiesUnbound = Number(res?.cleanup?.policies_unbound || 0);
      const cleanupBits = [
        zonesDisabled > 0 ? `${zonesDisabled} zone${zonesDisabled === 1 ? '' : 's'} disabled` : '',
        policiesUnbound > 0 ? `${policiesUnbound} polic${policiesUnbound === 1 ? 'y' : 'ies'} unbound` : '',
      ].filter(Boolean);
      setSnackbar({
        open: true,
        message: cleanupBits.length > 0
          ? `Removed camera ${deleteTarget.name || deleteTarget.camera_id} (${cleanupBits.join(', ')})`
          : `Removed camera ${deleteTarget.name || deleteTarget.camera_id}`,
        severity: 'success',
      });
      await fetchCameras();
    } catch (err: unknown) {
      setDeleteDialogError(formatRegistryCameraDeleteError(err));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleOpenViewer = async (camera: Camera) => {
    if (camera.enabled === false || camera.status === 'disabled') {
      setSnackbar({ open: true, message: 'Camera is disabled. Enable it to view live.', severity: 'error' });
      return;
    }
    setViewingCamera(camera);
    setViewerOpen(true);

    setMjpegLoading(true);
    setMjpegError(null);
    setIsLiveStream(false);
    setMjpegUrl(null);

    try {
      const mode = preferredMjpegModeForCamera(camera.camera_id);
      const streamStatus = await mjpegApi.startStream(camera.camera_id, undefined, camera.edge_device_id, mode);
      if (!(streamStatus.ok && streamStatus.mjpeg_url)) {
        setMjpegError(streamStatus.error || 'Failed to start stream');
        return;
      }

      // Wait for the first real frame before rendering the MJPEG <img>.
      // Otherwise the server will send placeholder bytes which render as a dark/blank view.
      const deadlineMs = Date.now() + (camera.camera_id.startsWith('cam_') ? 30_000 : 60_000);
      let ready = false;
      while (Date.now() < deadlineMs) {
        try {
          const snapshot = await mjpegApi.getSnapshot(camera.camera_id, camera.edge_device_id);
          if (snapshot.isRealFrame) {
            ready = true;
            break;
          }
        } catch {
          // Not ready yet (404) or transient.
        }
        await new Promise((r) => setTimeout(r, 750));
      }

      if (!ready) {
        setMjpegError('Stream is taking too long to produce first frame (try refresh)');
        const fallbackSnapshot = snapshotMap[camera.camera_id];
        if (fallbackSnapshot) {
          setMjpegUrl(fallbackSnapshot);
          setIsLiveStream(false);
        }
        return;
      }

      const baseUrl = window.location.origin;
      const sep = streamStatus.mjpeg_url.includes('?') ? '&' : '?';
      setMjpegUrl(`${baseUrl}${streamStatus.mjpeg_url}${sep}_=${Date.now()}`);
      setIsLiveStream(true);
    } catch (err: any) {
      setMjpegError(err.message || 'Failed to start stream');
      // Fallback snapshot
      const fallbackSnapshot = snapshotMap[camera.camera_id];
      if (fallbackSnapshot) {
        setMjpegUrl(fallbackSnapshot);
        setIsLiveStream(false);
        setMjpegError('Using last snapshot (MJPEG unavailable)');
      }
    } finally {
      setMjpegLoading(false);
    }
  };

  const handleCloseViewer = async () => {
    if (viewingCamera) {
      try {
        await mjpegApi.stopStream(viewingCamera.camera_id, viewingCamera.edge_device_id);
      } catch (err) {
        console.error('Failed to stop MJPEG stream:', err);
      }
    }
    setViewerOpen(false);
    setViewingCamera(null);
    setMjpegUrl(null);
    setMjpegError(null);
    setIsLiveStream(false);
    setMjpegLoading(false);
  };

  const handleVerifyCamera = useCallback(async (camera: Camera) => {
    setVerifySubmitting((prev) => ({ ...prev, [camera.camera_id]: true }));
    setVerifyTarget(camera);
    setVerifyResult(null);
    setVerifyDialogOpen(true);
    try {
      const res = await api.post(
        `/api/v1/sites/${siteId}/cameras/${encodeURIComponent(camera.camera_id)}/live_view/verify`,
        {
          edge_device_id: camera.edge_device_id,
          timeout_sec: 30,
        }
      );
      setVerifyResult(res?.data || null);
    } catch (err: any) {
      setVerifyResult(err?.response?.data || { ok: false, error: 'verify_failed', detail: String(err?.message || err) });
    } finally {
      setVerifySubmitting((prev) => ({ ...prev, [camera.camera_id]: false }));
    }
  }, [siteId]);

  const handleOpenLocationDialog = (camera: Camera) => {
    if (!siteId || !canWriteSites) return;
    const placement = locationLookups.cameraPlacementById?.[camera.camera_id];
    setLocationTarget(camera);
    setLocationAreaId(String(placement?.areaId || '').trim());
    setLocationMount(String(placement?.mountLabel || '').trim());
    setLocationError(null);
    setLocationDialogOpen(true);
    sitesApi.listAreas(siteId)
      .then((areas) => setLocationAreas(Array.isArray(areas) ? areas : []))
      .catch(() => setLocationAreas([]));
  };

  const handleSaveCameraLocation = async () => {
    if (!locationTarget || !siteId) return;
    const areaId = String(locationAreaId || '').trim();
    if (!areaId) {
      setLocationError('Area is required');
      return;
    }
    setLocationSaving(true);
    setLocationError(null);
    try {
      await sitesApi.putCameraPlacement(siteId, locationTarget.camera_id, {
        area_id: areaId,
        mount_label: String(locationMount || '').trim() || null,
        device_id: String(locationTarget.edge_device_id || '').trim() || null,
      });
      setLocationDialogOpen(false);
      setLocationTarget(null);
      refreshLocationLookups();
      setSnackbar({ open: true, message: 'Camera location updated', severity: 'success' });
    } catch (err: any) {
      const d = err?.response?.data;
      const msg =
        (typeof d?.detail === 'string' ? d.detail : null)
        || (typeof d?.error === 'string' ? d.error : null)
        || (typeof err?.message === 'string' ? err.message : null)
        || 'Failed to save camera location';
      setLocationError(msg);
    } finally {
      setLocationSaving(false);
    }
  };

  const handleOpenSnapshotEditor = (camera: Camera) => {
    if (camera.enabled === false || camera.status === 'disabled') {
      setSnackbar({ open: true, message: 'Camera is disabled. Enable it to edit zones/lines.', severity: 'error' });
      return;
    }
    setSnapshotEditorCamera(camera);
    setSnapshotEditorOpen(true);
  };

  const handleRefreshViewer = async () => {
    if (!viewingCamera) return;
    if (viewingCamera.enabled === false || viewingCamera.status === 'disabled') {
      setSnackbar({ open: true, message: 'Camera is disabled. Enable it to view live.', severity: 'error' });
      return;
    }

    setMjpegLoading(true);
    setMjpegError(null);
    setIsLiveStream(false);
    setMjpegUrl(null);

    try {
      const mode = preferredMjpegModeForCamera(viewingCamera.camera_id);
      const streamStatus = await mjpegApi.startStream(viewingCamera.camera_id, undefined, viewingCamera.edge_device_id, mode);
      if (!(streamStatus.ok && streamStatus.mjpeg_url)) {
        setMjpegError(streamStatus.error || 'Failed to start stream');
        return;
      }

      const deadlineMs = Date.now() + (viewingCamera.camera_id.startsWith('cam_') ? 30_000 : 60_000);
      let ready = false;
      while (Date.now() < deadlineMs) {
        try {
          const snapshot = await mjpegApi.getSnapshot(viewingCamera.camera_id, viewingCamera.edge_device_id);
          if (snapshot.isRealFrame) {
            ready = true;
            break;
          }
        } catch {
          // Not ready yet (404) or transient.
        }
        await new Promise((r) => setTimeout(r, 750));
      }

      if (!ready) {
        setMjpegError('Stream is taking too long to produce first frame (try refresh)');
        const fallbackSnapshot = snapshotMap[viewingCamera.camera_id];
        if (fallbackSnapshot) {
          setMjpegUrl(fallbackSnapshot);
          setIsLiveStream(false);
        }
        return;
      }

      const baseUrl = window.location.origin;
      const sep = streamStatus.mjpeg_url.includes('?') ? '&' : '?';
      setMjpegUrl(`${baseUrl}${streamStatus.mjpeg_url}${sep}_=${Date.now()}`);
      setIsLiveStream(true);
    } catch (err: any) {
      setMjpegError(err.message || 'Failed to start stream');
      const fallbackSnapshot = snapshotMap[viewingCamera.camera_id];
      if (fallbackSnapshot) {
        setMjpegUrl(fallbackSnapshot);
        setIsLiveStream(false);
        setMjpegError('Using last snapshot (MJPEG unavailable)');
      }
    } finally {
      setMjpegLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {canReadCameras && !canWriteCameras && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only camera access. Viewing and verification are allowed; camera setup and state changes are disabled for this account.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Cameras
        </Typography>
        <Box>
          <Tooltip title="Open documentation (Cameras)">
            <IconButton
              component="a"
              href="/docs/user-guide/configuration/cameras/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DocsIcon />
            </IconButton>
          </Tooltip>
          <RefreshIconButton
            onClick={() => fetchCameras().catch(() => undefined)}
            loading={refreshing}
            disabled={loading}
            title="Refresh cameras"
            size="medium"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)} sx={{ ml: 2 }} disabled={!canWriteCameras}>
            Add Camera
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {pollBackoffUntil && Date.now() < pollBackoffUntil && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Rate limited. Backing off until {new Date(pollBackoffUntil).toLocaleTimeString()}.
        </Alert>
      )}

      {loading && displayedCameras.length === 0 ? (
        <Grid container spacing={3}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
                <CardActions>
                  <Skeleton variant="rounded" width={80} height={30} />
                  <Skeleton variant="rounded" width={80} height={30} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FailedIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Unable to load cameras
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchCameras().catch(() => undefined)}>
            Retry
          </Button>
        </Paper>
      ) : cameras.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CameraIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No cameras configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first camera to start monitoring
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)} disabled={!canWriteCameras}>
            Add Camera
          </Button>
        </Paper>
      ) : displayedCameras.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CameraIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Waiting for live cameras
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hiddenCameraCount > 0
              ? `${hiddenCameraCount} configured camera${hiddenCameraCount === 1 ? '' : 's'} will appear here as soon as they report ready or produce a fresh snapshot.`
              : 'Configured cameras will appear here as soon as they report ready or produce a fresh snapshot.'}
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchCameras().catch(() => undefined)}>
            Refresh
          </Button>
        </Paper>
      ) : (
        <>
          {hiddenCameraCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing live cameras only. {hiddenCameraCount} configured camera{hiddenCameraCount === 1 ? '' : 's'} will appear automatically when they come online.
            </Alert>
          )}
        <Grid container spacing={3}>
          {displayedCameras.map((camera: Camera) => {
            const status = statusMap[camera.camera_id] || { status: camera.status || 'unknown' };
            const snapshot = snapshotMap[camera.camera_id];
            const displayStatus = status.status || camera.status || 'unknown';
            const snapTs = snapshotAgeMap[camera.camera_id];
            const enabled = camera.enabled !== false && displayStatus !== 'disabled';
            const stale = snapTs ? Date.now() - snapTs > SNAPSHOT_STALE_MS : false;
            const hasFreshSnapshot = !!snapTs && !stale;
            // F-30: Use standardized status metadata
            const healthState = (status as any)?.health_state || (camera as any)?.health_state || {};
            let hs = (String(healthState?.state || '').toUpperCase() || 'DEGRADED') as CameraHealthState;
            let statusMeta = getCameraHealthMetadata(hs);
            let hsReason = healthState?.reason || statusMeta.reason;
            let hsFixHref = healthState?.fix?.href || statusMeta.remediationAction?.path;

            // If we are actively rendering a fresh snapshot, prefer that signal over potentially stale backend health_state.
            // This prevents confusing "STREAM_ERROR/DEGRADED" badges when the operator can clearly see live frames.
            if (enabled && hasFreshSnapshot) {
              hs = 'READY' as CameraHealthState;
              statusMeta = getCameraHealthMetadata(hs);
              hsReason = 'Snapshot is fresh';
              hsFixHref = null;
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={camera.camera_id}>
                <Card>
                  {snapshot ? (
                    <CardMedia
                      component="img"
                      height="400"
                      image={snapshot}
                      alt={camera.name}
                      sx={{ objectFit: 'contain', bgcolor: 'black' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                      }}
                    >
                      <CameraIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    </Box>
                  )}

                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {camera.name || camera.camera_id}
                      </Typography>
                      <Tooltip title={displayStatus}>
                        <Box>{getStatusIcon(displayStatus)}</Box>
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip label={hs} color={getHealthStateColor(hs) as any} size="small" />
                      {hsFixHref && (
                        <Button size="small" variant="outlined" onClick={() => navigate(hsFixHref)}>
                          Fix
                        </Button>
                      )}
                    </Box>
                    {hsReason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {hsReason}
                      </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Device:</strong> {camera.edge_device_id}
                    </Typography>
                    <CameraLocationPath
                      siteDisplayName={locationLookups.siteDisplayName}
                      siteId={locationLookups.siteId || siteId}
                      placement={locationLookups.cameraPlacementById?.[camera.camera_id]}
                      canWrite={canWriteSites}
                      onEdit={() => handleOpenLocationDialog(camera)}
                    />
                    {clockStatusByDevice[camera.edge_device_id] === true && (
                      <Box sx={{ mb: 1 }}>
                        <Chip label="Clock unsynced" color="warning" size="small" variant="outlined" />
                      </Box>
                    )}

                    {/* F-06: Display device heartbeat status */}
                    {(() => {
                      const hs = healthState || {};
                      const deviceLastSeen = hs.device_last_seen;
                      const ageS = hs.age_s;
                      if (deviceLastSeen !== undefined && ageS !== null) {
                        let heartbeatMsg = '';
                        if (ageS < 60) {
                          heartbeatMsg = `${Math.floor(ageS)}s ago`;
                        } else if (ageS < 3600) {
                          heartbeatMsg = `${Math.floor(ageS / 60)}m ago`;
                        } else {
                          heartbeatMsg = `${Math.floor(ageS / 3600)}h ago`;
                        }
                        // F-06: Heartbeat is OK if age < 5 minutes and device status indicates online
                        // Also check if the health state reason indicates device is online
                        const deviceStatus = hs.device_status || '';
                        const isOnline = deviceStatus.toLowerCase() === 'online' || deviceStatus.toLowerCase() === 'running';
                        const reasonIndicatesOnline = (hs.reason || '').toLowerCase().includes('device online') || (hs.reason || '').toLowerCase().includes('heartbeat');
                        const heartbeatOk = ageS !== null && ageS < 300 && (isOnline || reasonIndicatesOnline);
                        const heartbeatLine = (
                          <Typography
                            variant="body2"
                            color={heartbeatOk ? 'success.main' : 'warning.main'}
                            gutterBottom
                          >
                            <strong>Heartbeat:</strong> {heartbeatOk ? '✓' : '✗'} {heartbeatMsg}
                          </Typography>
                        );
                        if (hasFreshSnapshot && !heartbeatOk) {
                          return (
                            <Tooltip
                              title={
                                'This time comes from devices.last_seen, updated when the edge calls the cloud heartbeat API (POST …/devices/<id>/heartbeat). ' +
                                'A fresh snapshot only means RTSP/snapshot succeeded from the cloud; it does not update last_seen. ' +
                                'If the Pi is on but this is old, fix heartbeat on the edge (URL, device_id, schedule, or network).'
                              }
                              placement="top"
                              arrow
                            >
                              <span style={{ display: 'block', width: '100%' }}>{heartbeatLine}</span>
                            </Tooltip>
                          );
                        }
                        return heartbeatLine;
                      }
                      return null;
                    })()}

                    {camera.rtsp_url && (
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ wordBreak: 'break-all' }}>
                        <strong>RTSP:</strong> {camera.rtsp_url}
                      </Typography>
                    )}

                    {/* Offering mode selector (Warehouse vs OCR).
                        This writes a per-camera pipeline override in the cloud registry.
                        Cloud+edge guardrails prevent running both offerings together on one device. */}
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <FormControl fullWidth size="small" disabled={!enabled || Boolean(pipelineSubmitting[`${camera.edge_device_id}:${camera.camera_id}`])}>
                        <InputLabel id={`mode-label-${camera.camera_id}`}>Mode</InputLabel>
                        <Select
                          labelId={`mode-label-${camera.camera_id}`}
                          label="Mode"
                          value={(String(camera.pipeline || '').toLowerCase() === 'ocr_stream') ? 'ocr' : 'warehouse'}
                          onChange={(e) => {
                            const v = String(e.target.value || 'warehouse') as 'warehouse' | 'ocr';
                            void setCameraMode(camera, v);
                          }}
                        >
                          <MenuItem value="warehouse">Warehouse</MenuItem>
                          <MenuItem value="ocr">OCR</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ mt: 1, mb: 1 }}>
                      <FormControl fullWidth size="small" disabled={!enabled || Boolean(pipelineSubmitting[`${camera.edge_device_id}:${camera.camera_id}`])}>
                        <InputLabel id={`profile-label-${camera.camera_id}`}>Epic 46 Profile</InputLabel>
                        <Select
                          labelId={`profile-label-${camera.camera_id}`}
                          label="Epic 46 Profile"
                          value={(camera.profile_id || 'A') as 'A' | 'B' | 'C' | 'D'}
                          onChange={(e) => {
                            const v = String(e.target.value || 'A').toUpperCase() as 'A' | 'B' | 'C' | 'D';
                            void setCameraProfile(camera, v);
                          }}
                        >
                          {profileOptions.map((p) => (
                            <MenuItem key={p.profile_id} value={p.profile_id}>
                              {p.profile_id} - {p.profile_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {status.last_frame && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Last Frame:</strong> {formatTimestamp(status.last_frame)}
                      </Typography>
                    )}

                    {/* F-08: Last snapshot display with Fresh/Stale badge */}
                    {snapTs && (() => {
                      const ageMs = Date.now() - snapTs;
                      const ageSeconds = Math.floor(ageMs / 1000);
                      const ageMinutes = Math.floor(ageSeconds / 60);
                      const ageHours = Math.floor(ageMinutes / 60);
                      let ageText = '';
                      if (ageSeconds < 60) {
                        ageText = `${ageSeconds}s ago`;
                      } else if (ageMinutes < 60) {
                        ageText = `${ageMinutes}m ago`;
                      } else {
                        ageText = `${ageHours}h ago`;
                      }
                      const isStale = ageMs > 60000; // Stale if > 1 minute
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" color={isStale ? 'warning.main' : 'text.secondary'}>
                            <strong>Last snapshot:</strong> {ageText}
                          </Typography>
                          {isStale ? (
                            <Chip label="Stale" color="warning" size="small" variant="outlined" />
                          ) : (
                            <Chip label="Fresh" color="success" size="small" variant="outlined" />
                          )}
                        </Box>
                      );
                    })()}

                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabled}
                          onChange={() => {
                            setToggleTarget(camera);
                            setToggleEnabledTarget(!enabled);
                            setToggleReason('');
                            setToggleDialogOpen(true);
                          }}
                          disabled={!canWriteCameras}
                        />
                      }
                      label={enabled ? 'Enabled' : 'Disabled'}
                    />

                    {status.last_error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {status.last_error}
                      </Alert>
                    )}
                  </CardContent>

                    <CardActions>
                    {/* F-02: State-based primary CTA */}
                    {(() => {
                      const setupStatus = cameraSetupStatus[camera.camera_id];
                      const hasGeometryOrPolicies = setupStatus?.hasGeometry || setupStatus?.hasPolicies;
                      
                      // Primary CTA: Setup if no geometry/policies, otherwise View Live
                      if (!hasGeometryOrPolicies) {
                        return (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenSnapshotEditor(camera)}
                              disabled={!enabled || !canWriteCameras}
                              sx={{ flexGrow: 1 }}
                            >
                              Setup Camera
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ViewIcon />}
                              onClick={() => handleOpenViewer(camera)}
                              disabled={!enabled}
                            >
                              View Live
                            </Button>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<ViewIcon />}
                              onClick={() => handleOpenViewer(camera)}
                              disabled={!enabled}
                              sx={{ flexGrow: 1 }}
                            >
                              View Live
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenSnapshotEditor(camera)}
                              disabled={!enabled || !canWriteCameras}
                            >
                              Edit Geometry
                            </Button>
                          </>
                        );
                      }
                    })()}
                    
                    {/* F-08: Per-camera refresh snapshot button */}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleVerifyCamera(camera)}
                      disabled={!enabled || Boolean(verifySubmitting[camera.camera_id])}
                    >
                      {verifySubmitting[camera.camera_id] ? 'Verifying…' : 'Verify'}
                    </Button>
                    <Tooltip title="Refresh snapshot">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            // F-08: Manual refresh should request a fresh capture (not cached thumbnail).
                            const snap = await camerasApi.getCameraSnapshot(camera.camera_id, camera.edge_device_id);
                            setSnapshotMap((prev) => ({ ...prev, [camera.camera_id]: snap }));
                            setSnapshotAgeMap((prev) => ({ ...prev, [camera.camera_id]: Date.now() }));
                          } catch (err) {
                            console.error('Failed to refresh snapshot:', err);
                          }
                        }}
                        disabled={!enabled}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<PolicyIcon />}
                      onClick={() =>
                        navigate(
                          `/policies?camera_id=${encodeURIComponent(camera.camera_id)}&device_id=${encodeURIComponent(
                            camera.edge_device_id
                          )}`
                        )
                      }
                    >
                      Policies
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AuditIcon />}
                      onClick={() =>
                        navigate(`/audit?resource_type=camera&resource_id=${encodeURIComponent(camera.camera_id)}`)
                      }
                    >
                      Audit
                    </Button>
                    {grafanaBase && (
                      <Tooltip title="View in Grafana">
                        <IconButton
                          size="small"
                          component="a"
                          target="_blank"
                          rel="noreferrer"
                          href={`${grafanaBase}/d/warehouse-safety?var-device_id=${encodeURIComponent(
                            camera.edge_device_id
                          )}&var-camera_id=${encodeURIComponent(camera.camera_id)}&from=now-6h&to=now`}
                        >
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove camera">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRequestDeleteCamera(camera)}
                          disabled={!canWriteCameras || deleteSubmitting}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        </>
      )}

      <ZoneSnapshotEditorDialog
        open={snapshotEditorOpen}
        onClose={() => {
          setSnapshotEditorOpen(false);
          const camera = snapshotEditorCamera;
          setSnapshotEditorCamera(null);
          // F-02: Refresh camera setup status after geometry changes
          if (camera) {
            void checkCameraSetupStatus([camera]);
          }
        }}
        siteId={siteId}
        initialEdgeDeviceId={snapshotEditorCamera?.edge_device_id}
        initialCameraId={snapshotEditorCamera?.camera_id}
        initialFullScreen={true}
      />

      {/* Enable/Disable Dialog */}
      <Dialog open={toggleDialogOpen} onClose={() => setToggleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{toggleEnabledTarget ? 'Enable Camera' : 'Disable Camera'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Camera: <strong>{toggleTarget?.name || toggleTarget?.camera_id}</strong>
          </Typography>
          {!toggleEnabledTarget && (
            <TextField
              fullWidth
              label="Reason (recommended)"
              value={toggleReason}
              onChange={(e: any) => setToggleReason(e.target.value)}
              placeholder="e.g., maintenance, misbehaving stream, privacy"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialogOpen(false)} disabled={toggleSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={toggleEnabledTarget ? 'success' : 'warning'}
            disabled={!canWriteCameras || !toggleTarget || toggleSubmitting}
            onClick={async () => {
              if (!toggleTarget) return;
              setToggleSubmitting(true);
              try {
                const res = await camerasApi.setCameraEnabled(
                  siteId,
                  toggleTarget.camera_id,
                  toggleTarget.edge_device_id,
                  toggleEnabledTarget,
                  toggleReason || undefined
                );
                if (res?.ok) {
                  setSnackbar({
                    open: true,
                    message: toggleEnabledTarget ? 'Camera enabled' : 'Camera disabled',
                    severity: 'success',
                  });
                  setToggleDialogOpen(false);
                  await fetchCameras();
                } else {
                  setSnackbar({ open: true, message: res?.error || 'Failed to update camera state', severity: 'error' });
                }
              } catch (e: any) {
                setSnackbar({
                  open: true,
                  message: e?.response?.data?.detail || e?.message || 'Failed to update camera state',
                  severity: 'error',
                });
              } finally {
                setToggleSubmitting(false);
              }
            }}
          >
            {toggleSubmitting ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteSubmitting) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
          setDeleteDialogError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{deleteDialogError ? "Can't remove camera" : 'Remove Camera'}</DialogTitle>
        <DialogContent>
          {deleteDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Remove blocked</AlertTitle>
              {deleteDialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Remove <strong>{deleteTarget?.name || deleteTarget?.camera_id}</strong> from device{' '}
            <strong>{deleteTarget?.edge_device_id}</strong>? This updates desired state and removes the
            camera from the cloud UI.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
              setDeleteDialogError(null);
            }}
            disabled={deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteCamera}
            disabled={!canWriteCameras || !deleteTarget || deleteSubmitting}
          >
            {deleteSubmitting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <CameraLocationDialog
        open={locationDialogOpen}
        title={locationTarget ? `Set location — ${locationTarget.name || locationTarget.camera_id}` : 'Set location'}
        areaId={locationAreaId}
        mountLabel={locationMount}
        areas={locationAreas}
        error={locationError}
        saving={locationSaving}
        canWrite={canWriteSites}
        onClose={() => setLocationDialogOpen(false)}
        onAreaChange={setLocationAreaId}
        onMountChange={setLocationMount}
        onSave={handleSaveCameraLocation}
      />

      {/* Add Camera Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Camera</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Camera Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Edge Device"
              value={formData.edge_device_id}
              onChange={(e) => setFormData({ ...formData, edge_device_id: e.target.value })}
              margin="normal"
              required
              select
              SelectProps={{ native: true }}
            >
              <option value="">Select device...</option>
              {devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.name} ({d.device_id})
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="RTSP URL"
              value={formData.rtsp_url}
              onChange={(e) => setFormData({ ...formData, rtsp_url: e.target.value })}
              margin="normal"
              required
              placeholder="rtsp://host:port/path"
              helperText="Enter RTSP URL (credentials can be added below or in URL)"
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="RTSP Username (optional)"
                value={formData.rtsp_username}
                onChange={(e) => setFormData({ ...formData, rtsp_username: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="RTSP Password (optional)"
                type="password"
                value={formData.rtsp_password}
                onChange={(e) => setFormData({ ...formData, rtsp_password: e.target.value })}
                margin="normal"
              />
            </Box>

            <FormControl fullWidth margin="normal">
              <InputLabel id="add-camera-profile-label">Epic 46 Profile</InputLabel>
              <Select
                labelId="add-camera-profile-label"
                label="Epic 46 Profile"
                value={(formData.profile_id || 'A') as 'A' | 'B' | 'C' | 'D'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    profile_id: String(e.target.value || 'A').toUpperCase() as 'A' | 'B' | 'C' | 'D',
                  })
                }
              >
                {profileOptions.map((p) => (
                  <MenuItem key={p.profile_id} value={p.profile_id}>
                    {p.profile_id} - {p.profile_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Pipeline (optional)"
              value={formData.pipeline || ''}
              onChange={(e) => setFormData({ ...formData, pipeline: e.target.value })}
              margin="normal"
              helperText="Leave empty for default. RTSP analytics: use detection_pose_ppe_rtsp_reid. Forklift RTSP: use detection_pose_ppe_rtsp_reid_forklift_roi_confirm. Legacy heavy graph names are mapped automatically."
            />

            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={handleTestCamera}
                disabled={!canWriteCameras || testing || !formData.rtsp_url || !formData.edge_device_id}
                fullWidth
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>

            {testing && <LinearProgress sx={{ mb: 2 }} />}

            {testResult && (
              <Alert
                severity={testResult.ok ? 'success' : 'error'}
                sx={{ mt: 2 }}
                action={testResult.ok ? <Chip label="Ready" color="success" size="small" /> : <Chip label="Failed" color="error" size="small" />}
              >
                {testResult.ok ? (
                  <Box>
                    <Typography variant="body2">
                      <strong>Connection successful!</strong>
                    </Typography>
                    {testResult.resolution && <Typography variant="body2">Resolution: {testResult.resolution}</Typography>}
                    {testResult.codec && <Typography variant="body2">Codec: {testResult.codec}</Typography>}
                    {testResult.fps && <Typography variant="body2">FPS: {testResult.fps.toFixed(1)}</Typography>}
                    {testResult.latency_ms && <Typography variant="body2">Latency: {testResult.latency_ms}ms</Typography>}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2">
                      <strong>Connection failed:</strong> {testResult.error || testResult.detail || 'Unknown error'}
                    </Typography>
                    {testResult.edge_result && (
                      <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        {JSON.stringify(testResult.edge_result, null, 2)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleAddCamera}
            variant="contained"
            disabled={!canWriteCameras || submitting || !formData.name || !formData.rtsp_url || !formData.edge_device_id}
          >
            {submitting ? 'Adding...' : 'Add & Deploy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Verify camera dialog */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verify camera {verifyTarget?.camera_id || ''}</DialogTitle>
        <DialogContent>
          {!verifyResult ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Running tri-probe (ingest/output/view)…
              </Typography>
            </Box>
          ) : (
            <>
              <Alert severity={verifyResult?.verified ? 'success' : 'warning'} sx={{ mb: 2 }}>
                <AlertTitle>{verifyResult?.verified ? 'Verified' : 'Not verified'}</AlertTitle>
                {verifyResult?.error ? `${verifyResult.error}${verifyResult.detail ? `: ${verifyResult.detail}` : ''}` : 'Completed'}
              </Alert>

              {(() => {
                const probes = verifyResult?.probes || {};
                const ingest = probes?.ingest || {};
                const view = probes?.view || {};
                const tf = ingest?.detail?.test_feed || ingest?.detail?.test_feed_result || ingest?.detail;
                const ff = tf?.ffprobe || tf?.detail?.ffprobe;
                const stream = Array.isArray(ff?.streams) ? ff.streams[0] : undefined;
                const decode = tf?.decode || tf?.detail?.decode;
                const res = stream?.width && stream?.height ? `${stream.width}x${stream.height}` : 'N/A';
                const fps = decode?.fps ?? 'N/A';
                const latency = decode?.first_frame_latency_ms ?? 'N/A';
                const lastPts = decode?.last_pts_time ?? 'N/A';
                return (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Measured (edge ingest)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Resolution</Typography>
                        <Typography variant="body1">{res}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">FPS (decode)</Typography>
                        <Typography variant="body1">{String(fps)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">First frame latency</Typography>
                        <Typography variant="body1">{String(latency)}{latency !== 'N/A' ? ' ms' : ''}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Last frame PTS</Typography>
                        <Typography variant="body1">{String(lastPts)}</Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Dropped frames: N/A (probe does not currently report drop count)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last frame timestamp: {view?.detail?.last_frame_at || view?.detail?.snapshot_ts || 'N/A'}
                    </Typography>
                  </Paper>
                );
              })()}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Raw probe output
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(verifyResult, null, 2)}
                </pre>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)} startIcon={<CloseIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Viewer Dialog */}
      <Dialog open={viewerOpen} onClose={handleCloseViewer} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: 'black', color: 'white' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'rgba(0,0,0,0.8)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CameraIcon />
            <Typography variant="h6">{viewingCamera?.name || viewingCamera?.camera_id}</Typography>
            <Chip
              label={
                viewingCamera
                  ? statusMap[viewingCamera.camera_id]?.status || viewingCamera.status || 'unknown'
                  : 'unknown'
              }
              color={
                getStatusColor(
                  viewingCamera
                    ? statusMap[viewingCamera.camera_id]?.status || viewingCamera.status || 'unknown'
                    : 'unknown'
                )
              }
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {mjpegLoading && <CircularProgress size={20} sx={{ color: 'white' }} />}
            <IconButton onClick={handleRefreshViewer} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleCloseViewer} sx={{ color: 'white' }}>
              <SettingsIcon />
            </IconButton>
            <IconButton onClick={handleCloseViewer} sx={{ color: 'white' }} aria-label="Close live view">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'black',
            position: 'relative',
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {mjpegUrl ? (
            <Box sx={{ position: 'relative', width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={mjpegUrl}
                alt="Live Stream"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                onError={async () => {
                  // Self-heal common cases:
                  // - token expires (browser <img> cannot attach headers)
                  // - transient RTSP pull failure / ffmpeg restart
                  if (mjpegLoading) return;
                  setMjpegError('Failed to load stream (retrying…)');
                  await handleRefreshViewer();
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.7)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  color: 'white',
                  fontSize: '0.75rem',
                }}
              >
                {isLiveStream ? 'Live Stream (MJPEG)' : 'Static Snapshot (Fallback)'}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
              {mjpegLoading ? (
                <>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography>Starting live stream...</Typography>
                </>
              ) : mjpegError ? (
                <Box>
                  <Typography color="error" sx={{ mb: 2 }}>
                    {mjpegError}
                  </Typography>
                  <Button variant="contained" onClick={handleRefreshViewer} sx={{ mt: 2 }}>
                    Retry
                  </Button>
                </Box>
              ) : (
                <>
                  <CameraIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    Stream not started
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Click refresh to start the live stream.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {viewingCamera && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                bgcolor: 'rgba(0,0,0,0.7)',
                p: 1,
                borderRadius: 1,
                color: 'white',
              }}
            >
              <Typography variant="body2">
                <strong>Device:</strong> {viewingCamera.edge_device_id}
              </Typography>
              {viewingCamera.rtsp_url && (
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  <strong>RTSP:</strong> {viewingCamera.rtsp_url}
                </Typography>
              )}
              {statusMap[viewingCamera.camera_id]?.last_frame && (
                <Typography variant="body2">
                  <strong>Last Frame:</strong> {formatTimestamp(statusMap[viewingCamera.camera_id].last_frame)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)' }}>
          <Button onClick={handleCloseViewer} sx={{ color: 'white' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cameras;
